import React, { useState } from 'react';
import { Sidebar } from '../components/Sidebar';
import { Header } from '../components/Header';
import { StepIndicator } from '../components/StepIndicator';
import { UploadIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
const STEPS = ['Master Creative', 'Campaign Setup', 'Select Creative', 'Styling Template', 'Variable Content', 'Generate', 'Review & Publish'];
export function VariableContent() {
  const navigate = useNavigate();
  const [pricingMode, setPricingMode] = useState<'same' | 'regional' | 'csv'>('same');
  const [offerMode, setOfferMode] = useState<'same' | 'regional' | 'csv'>('same');
  const [festivalMode, setFestivalMode] = useState<'same' | 'regional' | 'csv'>('same');
  const [pricing, setPricing] = useState('');
  const [offer, setOffer] = useState('');
  const [festival, setFestival] = useState('');
  const canContinue = pricing || offer || festival;
  return <div className="flex w-full min-h-screen bg-gray-50" data-id="element-3704">
      <Sidebar data-id="element-3705" />
      <main className="flex-1" data-id="element-3706">
        <Header data-id="element-3707" />
        <StepIndicator currentStep={5} steps={STEPS} data-id="element-3708" />
        <div className="px-6 py-8" data-id="element-3709">
          <div className="max-w-4xl mx-auto" data-id="element-3710">
            <h1 className="text-2xl font-semibold text-gray-800 mb-2" data-id="element-3711">
              Step 4: Fill Variable Content
            </h1>
            <p className="text-gray-600 mb-6" data-id="element-3712">
              Configure pricing, offers, and festival text for your campaigns
            </p>

            <div className="space-y-6" data-id="element-3713">
              {/* State-wise Pricing */}
              <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-3714">
                <h3 className="font-medium text-gray-800 mb-4" data-id="element-3715">
                  A. State-wise Pricing
                </h3>
                <div className="flex space-x-3 mb-4" data-id="element-3716">
                  <button onClick={() => setPricingMode('same')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${pricingMode === 'same' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-3717">
                    Same for All
                  </button>
                  <button onClick={() => setPricingMode('regional')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${pricingMode === 'regional' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-3718">
                    Region-wise
                  </button>
                  <button onClick={() => setPricingMode('csv')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${pricingMode === 'csv' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-3719">
                    Upload CSV
                  </button>
                </div>

                {pricingMode === 'same' && <input type="text" value={pricing} onChange={e => setPricing(e.target.value)} placeholder="e.g., Starting at ₹12,50,000" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-3720" />}

                {pricingMode === 'regional' && <div className="space-y-3" data-id="element-3721">
                    <div className="grid grid-cols-2 gap-3" data-id="element-3722">
                      <div data-id="element-3723">
                        <label className="block text-sm text-gray-600 mb-1" data-id="element-3724">
                          North Zone
                        </label>
                        <input type="text" placeholder="₹12,50,000" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-3725" />
                      </div>
                      <div data-id="element-3726">
                        <label className="block text-sm text-gray-600 mb-1" data-id="element-3727">
                          South Zone
                        </label>
                        <input type="text" placeholder="₹12,75,000" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-3728" />
                      </div>
                      <div data-id="element-3729">
                        <label className="block text-sm text-gray-600 mb-1" data-id="element-3730">
                          East Zone
                        </label>
                        <input type="text" placeholder="₹12,60,000" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-3731" />
                      </div>
                      <div data-id="element-3732">
                        <label className="block text-sm text-gray-600 mb-1" data-id="element-3733">
                          West Zone
                        </label>
                        <input type="text" placeholder="₹12,80,000" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-3734" />
                      </div>
                    </div>
                  </div>}

                {pricingMode === 'csv' && <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center" data-id="element-3735">
                    <UploadIcon className="mx-auto text-gray-400 mb-3" size={32} data-id="element-3736" />
                    <p className="text-sm text-gray-600 mb-3" data-id="element-3737">
                      Upload CSV with dealer-specific pricing
                    </p>
                    <label className="inline-block bg-blue-500 text-white px-4 py-2 rounded-md text-sm cursor-pointer hover:bg-blue-600" data-id="element-3738">
                      Choose CSV File
                      <input type="file" accept=".csv" className="hidden" data-id="element-3739" />
                    </label>
                    <p className="text-xs text-gray-500 mt-2" data-id="element-3740">
                      Format: dealer_id, pricing
                    </p>
                  </div>}
              </div>

              {/* Offer/Discount Text */}
              <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-3741">
                <h3 className="font-medium text-gray-800 mb-4" data-id="element-3742">
                  B. Offer/Discount Text
                </h3>
                <div className="flex space-x-3 mb-4" data-id="element-3743">
                  <button onClick={() => setOfferMode('same')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${offerMode === 'same' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-3744">
                    Same for All
                  </button>
                  <button onClick={() => setOfferMode('regional')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${offerMode === 'regional' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-3745">
                    Region-wise
                  </button>
                  <button onClick={() => setOfferMode('csv')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${offerMode === 'csv' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-3746">
                    Dealer-specific CSV
                  </button>
                </div>

                {offerMode === 'same' && <textarea value={offer} onChange={e => setOffer(e.target.value)} placeholder="e.g., Get ₹50,000 off + Free accessories worth ₹25,000" rows={3} className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-3747" />}

                {offerMode === 'regional' && <div className="space-y-3" data-id="element-3748">
                    {['North', 'South', 'East', 'West'].map(zone => <div key={zone} data-id="element-3749">
                        <label className="block text-sm text-gray-600 mb-1" data-id="element-3750">
                          {zone} Zone Offer
                        </label>
                        <input type="text" placeholder={`Special offer for ${zone} zone`} className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-3751" />
                      </div>)}
                  </div>}

                {offerMode === 'csv' && <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center" data-id="element-3752">
                    <UploadIcon className="mx-auto text-gray-400 mb-3" size={32} data-id="element-3753" />
                    <p className="text-sm text-gray-600 mb-3" data-id="element-3754">
                      Upload CSV with dealer-specific offers
                    </p>
                    <label className="inline-block bg-blue-500 text-white px-4 py-2 rounded-md text-sm cursor-pointer hover:bg-blue-600" data-id="element-3755">
                      Choose CSV File
                      <input type="file" accept=".csv" className="hidden" data-id="element-3756" />
                    </label>
                  </div>}
              </div>

              {/* Festival Text */}
              <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-3757">
                <h3 className="font-medium text-gray-800 mb-4" data-id="element-3758">
                  C. Festival Text
                </h3>
                <div className="flex space-x-3 mb-4" data-id="element-3759">
                  <button onClick={() => setFestivalMode('same')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${festivalMode === 'same' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-3760">
                    Same for All
                  </button>
                  <button onClick={() => setFestivalMode('regional')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${festivalMode === 'regional' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-3761">
                    Regional Mapping
                  </button>
                  <button onClick={() => setFestivalMode('csv')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${festivalMode === 'csv' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-3762">
                    Dealer-specific CSV
                  </button>
                </div>

                {festivalMode === 'same' && <input type="text" value={festival} onChange={e => setFestival(e.target.value)} placeholder="e.g., Diwali Special Offer" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-3763" />}

                {festivalMode === 'regional' && <div className="space-y-3" data-id="element-3764">
                    <div data-id="element-3765">
                      <label className="block text-sm text-gray-600 mb-1" data-id="element-3766">
                        North India
                      </label>
                      <input type="text" placeholder="e.g., Diwali Dhamaka" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-3767" />
                    </div>
                    <div data-id="element-3768">
                      <label className="block text-sm text-gray-600 mb-1" data-id="element-3769">
                        South India
                      </label>
                      <input type="text" placeholder="e.g., Pongal Special" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-3770" />
                    </div>
                    <div data-id="element-3771">
                      <label className="block text-sm text-gray-600 mb-1" data-id="element-3772">
                        East India
                      </label>
                      <input type="text" placeholder="e.g., Durga Puja Offer" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-3773" />
                    </div>
                    <div data-id="element-3774">
                      <label className="block text-sm text-gray-600 mb-1" data-id="element-3775">
                        West India
                      </label>
                      <input type="text" placeholder="e.g., Ganesh Chaturthi Special" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-3776" />
                    </div>
                  </div>}

                {festivalMode === 'csv' && <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center" data-id="element-3777">
                    <UploadIcon className="mx-auto text-gray-400 mb-3" size={32} data-id="element-3778" />
                    <p className="text-sm text-gray-600 mb-3" data-id="element-3779">
                      Upload CSV with dealer-specific festival text
                    </p>
                    <label className="inline-block bg-blue-500 text-white px-4 py-2 rounded-md text-sm cursor-pointer hover:bg-blue-600" data-id="element-3780">
                      Choose CSV File
                      <input type="file" accept=".csv" className="hidden" data-id="element-3781" />
                    </label>
                  </div>}
              </div>
            </div>

            <div className="flex justify-between mt-6" data-id="element-3782">
              <button onClick={() => navigate('/dealerization/step-3')} className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-3783">
                Back
              </button>
              <button onClick={() => navigate('/dealerization/step-5')} disabled={!canContinue} className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed" data-id="element-3784">
                Continue to Generate Creatives
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>;
}